// Marketplace page (formerly Vaults)
import React, { useState } from 'react';
import { EXAMPLE_VAULTS } from './VaultBuilder.tsx';
import { EXAMPLE_STRATEGIES } from './StrategyBuilder.tsx';

// Mock public vaults
const PUBLIC_VAULTS = [
    {
        name: 'STRK Growth Vault',
        creator: '0x123...abcd',
        strategies: [
            { name: 'Leveraged STRK Staking', pct: 60 },
            { name: 'ETH/USDC LP DCA', pct: 40 },
        ],
        tvl: 120_000,
        subscribers: 38,
    },
    {
        name: 'Yield Diversified',
        creator: '0x456...ef12',
        strategies: [
            { name: 'Perps Yield Farming', pct: 50 },
            { name: 'ETH/USDC LP DCA', pct: 50 },
        ],
        tvl: 85_000,
        subscribers: 22,
    },
    {
        name: 'All Weather Vault',
        creator: '0x789...beef',
        strategies: [
            { name: 'Leveraged STRK Staking', pct: 34 },
            { name: 'ETH/USDC LP DCA', pct: 33 },
            { name: 'Perps Yield Farming', pct: 33 },
        ],
        tvl: 210_000,
        subscribers: 51,
    },
];

const Marketplace: React.FC = () => {
    // Mock: user's vaults (from EXAMPLE_VAULTS)
    const userVaults = EXAMPLE_VAULTS;
    const [selectedVault, setSelectedVault] = useState('');
    const [published, setPublished] = useState<string[]>([]);
    const [subscribeModal, setSubscribeModal] = useState<{ open: boolean; vault: any | null }>({ open: false, vault: null });
    const [mirrorModal, setMirrorModal] = useState<{ open: boolean; vault: any | null }>({ open: false, vault: null });
    const [subscribeAmount, setSubscribeAmount] = useState('');

    // Publish vault (mock)
    const handlePublish = () => {
        if (selectedVault && !published.includes(selectedVault)) {
            setPublished([...published, selectedVault]);
        }
    };

    // Subscribe (mock)
    const handleSubscribe = () => {
        setSubscribeModal({ open: false, vault: null });
        setSubscribeAmount('');
        // Show toast or confirmation (mock)
    };

    // Mirror (mock)
    const handleMirror = () => {
        setMirrorModal({ open: false, vault: null });
        // Show toast or confirmation (mock)
    };

    // Helper: get strategy details
    const getStrategy = (name: string) => EXAMPLE_STRATEGIES.find(s => s.name === name);

    return (
        <div className="p-6 text-white min-h-[80vh]">
            <h1 className="text-3xl font-bold mb-6">Vault Marketplace</h1>
            {/* Publish section */}
            <div className="bg-starkpanel rounded-xl shadow-glass p-6 mb-10 flex flex-col md:flex-row items-center gap-4">
                <div className="flex-1">
                    <label className="block text-lg font-semibold mb-2">Publish your vault</label>
                    <select
                        className="bg-gray-900 text-white rounded p-2 w-full"
                        value={selectedVault}
                        onChange={e => setSelectedVault(e.target.value)}
                    >
                        <option value="">Select a vault...</option>
                        {userVaults.map(v => (
                            <option key={v.name} value={v.name} disabled={published.includes(v.name)}>
                                {v.name} {published.includes(v.name) ? '(Published)' : ''}
                            </option>
                        ))}
                    </select>
                </div>
                <button
                    className="bg-accent hover:bg-accent2 text-starkbg font-bold px-6 py-3 rounded-lg shadow-glass mt-4 md:mt-0 disabled:opacity-50"
                    onClick={handlePublish}
                    disabled={!selectedVault || published.includes(selectedVault)}
                >
                    Publish
                </button>
            </div>
            {/* Public vaults grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {PUBLIC_VAULTS.map((vault, idx) => (
                    <div key={vault.name} className="bg-starkpanel rounded-xl shadow-glass p-6 flex flex-col gap-4">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-xl font-bold text-accent3">{vault.name}</span>
                            <span className="text-xs text-gray-400">by {vault.creator}</span>
                        </div>
                        <div className="mb-2">
                            <div className="flex flex-col gap-1">
                                {vault.strategies.map((s, i) => {
                                    const strat = getStrategy(s.name);
                                    return (
                                        <div key={s.name} className="flex items-center gap-2 text-sm">
                                            <span className="font-semibold text-accent2">{s.name}</span>
                                            <span className="text-gray-400">({s.pct}%)</span>
                                            {strat && (
                                                <>
                                                    <span className="ml-2 text-accent4">APY: {strat.apy}%</span>
                                                    <span className="ml-2 text-yellow-400">Risk: {strat.risk}</span>
                                                </>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                        <div className="flex items-center gap-4 text-sm mb-2">
                            <span className="text-green-400 font-semibold">TVL: ${vault.tvl.toLocaleString()}</span>
                            <span className="text-cyan-300">{vault.subscribers} subscribers</span>
                        </div>
                        <div className="flex gap-4 mt-auto">
                            <button
                                className="bg-accent3 hover:bg-accent2 text-starkbg font-bold px-4 py-2 rounded-lg shadow-glass flex-1"
                                onClick={() => setSubscribeModal({ open: true, vault })}
                            >
                                Subscribe
                            </button>
                            <button
                                className="bg-accent4 hover:bg-accent2 text-starkbg font-bold px-4 py-2 rounded-lg shadow-glass flex-1"
                                onClick={() => setMirrorModal({ open: true, vault })}
                            >
                                Mirror
                            </button>
                        </div>
                    </div>
                ))}
            </div>
            {/* Subscribe Modal */}
            {subscribeModal.open && (
                <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
                    <div className="bg-starkpanel rounded-xl shadow-glass p-8 w-full max-w-md">
                        <h2 className="text-xl font-bold mb-4">Subscribe to {subscribeModal.vault.name}</h2>
                        <input
                            className="bg-gray-900 text-white rounded p-2 w-full mb-4"
                            type="number"
                            placeholder="Amount to subscribe..."
                            value={subscribeAmount}
                            onChange={e => setSubscribeAmount(e.target.value)}
                        />
                        <div className="flex justify-end gap-2">
                            <button
                                className="px-4 py-2 rounded bg-gray-700 text-gray-200 hover:bg-gray-600"
                                onClick={() => setSubscribeModal({ open: false, vault: null })}
                            >
                                Cancel
                            </button>
                            <button
                                className="px-4 py-2 rounded bg-accent3 text-starkbg hover:bg-accent2 font-bold"
                                onClick={handleSubscribe}
                                disabled={!subscribeAmount}
                            >
                                Subscribe
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* Mirror Modal */}
            {mirrorModal.open && (
                <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
                    <div className="bg-starkpanel rounded-xl shadow-glass p-8 w-full max-w-md">
                        <h2 className="text-xl font-bold mb-4">Mirror {mirrorModal.vault.name}?</h2>
                        <div className="mb-4 text-gray-300">This will deploy a new vault with the same strategies and allocations.</div>
                        <div className="flex justify-end gap-2">
                            <button
                                className="px-4 py-2 rounded bg-gray-700 text-gray-200 hover:bg-gray-600"
                                onClick={() => setMirrorModal({ open: false, vault: null })}
                            >
                                Cancel
                            </button>
                            <button
                                className="px-4 py-2 rounded bg-accent4 text-starkbg hover:bg-accent2 font-bold"
                                onClick={handleMirror}
                            >
                                Mirror
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Marketplace; 